import React from 'react';
import { InputProps } from '../../types';

const Input: React.FC<InputProps> = ({
  type = 'text',
  size = 'md',
  label,
  placeholder,
  value,
  onChange,
  error,
  help,
  disabled = false,
  required = false,
  className = '',
  id,
}) => {
  const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
  
  const baseClasses = 'w-full border rounded-md transition-all duration-normal focus:outline-none focus:ring-2 focus:ring-offset-0';
  
  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-base',
    lg: 'px-5 py-4 text-lg',
  };
  
  const stateClasses = error
    ? 'border-error-300 focus:border-error-500 focus:ring-error-500'
    : 'border-neutral-300 focus:border-primary-500 focus:ring-primary-500';
  
  const disabledClasses = disabled
    ? 'bg-neutral-50 text-neutral-500 cursor-not-allowed'
    : 'bg-white text-neutral-900';
  
  const inputClasses = `
    ${baseClasses}
    ${sizeClasses[size]}
    ${stateClasses}
    ${disabledClasses}
    ${className}
  `.trim();
  
  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-neutral-700 mb-2"
        >
          {label}
          {required && <span className="text-error-500 ml-1">*</span>}
        </label>
      )}
      
      <input
        id={inputId}
        type={type}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={inputClasses}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={
          error ? `${inputId}-error` : help ? `${inputId}-help` : undefined
        }
      />
      
      {error && (
        <p
          id={`${inputId}-error`}
          className="text-error-600 text-sm mt-1"
          role="alert"
        >
          {error}
        </p>
      )}
      
      {help && !error && (
        <p
          id={`${inputId}-help`}
          className="text-neutral-500 text-sm mt-1"
        >
          {help}
        </p>
      )}
    </div>
  );
};

export default Input;
